/* eslint-disable @typescript-eslint/no-explicit-any */
import axiosInstance from './axioInstance';
import { useQuery, useMutation } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_INTEGRATIONS: `/me/integrations`,
  GET_CALENDAR_INTEGRATIONS: `/calendar`,
  POST_AUTHORISE_CALENDAR: `/calendar/authorize`,
  POST_AUTHORISE_SALESFORCE: `/salesforce/authorize`,
  GET_AUTHORISE_CALLBACK: (queryString: string) => `/calendar/callback?${queryString}`,
  PUT_CONNECT_DISCONNECT_CALENDAR: `/calendar`,
  DELETE_CALENDAR: `/calendar`,
  POST_FETCH_CALENDAR: `/calendar/fetch`,
  ACCOUNT_TRACKING: `/account/topic-text`,
});

const getIntegrations = async (): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_INTEGRATIONS}`);
  return response;
};

const useGetIntegrations = (enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_integrations'],
    queryFn: () => getIntegrations(),
    enabled: enabled
  })
}


const getCalendars = async (impersonate_email?: string): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_CALENDAR_INTEGRATIONS}`, {
    params: {
      ...(impersonate_email && { impersonate_email }),
    },
});
  return response;
};

const useGetCalendars = (enabled: boolean, impersonate_email?: string) => {
  return useQuery<string>({
    queryKey: ['get_calendar_integrations', impersonate_email],
    queryFn: () => getCalendars(impersonate_email),
    enabled: enabled
  })
}

const authoriseCalendar = async (authoriseCalendarParams: { calendar_domain: string }): Promise<any> => {
  const response = await axiosInstance.post(PATHS.POST_AUTHORISE_CALENDAR, authoriseCalendarParams);
  return response;
};

const useAuthoriseCalendar = (authoriseCalendarParams: { calendar_domain: string }, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['post_authorise_calendar'],
    queryFn: () => authoriseCalendar(authoriseCalendarParams),
    enabled: enabled
  })
}

const authoriseSalesforce = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.POST_AUTHORISE_SALESFORCE);
  return response;
};

const useAuthoriseSalesforce = (enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['post_authorise_salesforce'],
    queryFn: () => authoriseSalesforce(),
    enabled: enabled
  })
}

const authoriseCallback = async (queryString: string): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_AUTHORISE_CALLBACK(queryString)}`);
  return response;
};

const useAuthoriseCallback = (queryString: string, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_callback_calendar'],
    queryFn: () => authoriseCallback(queryString),
    enabled: enabled
  })
}

const connectDisconnectCalendar = async (connectDisconnectCalendarParams: { email_id: string, calendar_domain: string, is_active: boolean }) => {
  const response = await axiosInstance.put(PATHS.PUT_CONNECT_DISCONNECT_CALENDAR, connectDisconnectCalendarParams);
  return response;
}

const useCalendarConnectionMutation = (email_id: string, handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ['connect_disconnect_calendar', email_id],
    mutationFn: (connectDisconnectCalendarParams: { email_id: string, calendar_domain: string, is_active: boolean }) => connectDisconnectCalendar(connectDisconnectCalendarParams).then((res) => res),
    ...handlerFunctions
  })
}

const deleteCalendar = async (deleteCalendarParams: { email_id: string, calendar_domain: string }) => {
  const response = await axiosInstance.delete(PATHS.DELETE_CALENDAR, { data: deleteCalendarParams });
  return response;
}

const useCalendarDeleteMutation = (email_id: string, handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ['delete_calendar', email_id],
    mutationFn: (deleteCalendarParams: { email_id: string, calendar_domain: string }) => deleteCalendar(deleteCalendarParams).then((res) => res),
    ...handlerFunctions
  })
}

const fetchCalendar = async (fetchCalendarParams: { email_id: string, calendar_domain: string, start_time: string, end_time: string }): Promise<any> => {
  const response = await axiosInstance.post(PATHS.POST_FETCH_CALENDAR, fetchCalendarParams);
  return response;
};

const useFetchCalendar = (fetchCalendarParams: { email_id: string, calendar_domain: string, start_time: string, end_time: string }, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['fetch_calendar', `${fetchCalendarParams.email_id}_${fetchCalendarParams.start_time}_${fetchCalendarParams.end_time}`],
    queryFn: () => fetchCalendar(fetchCalendarParams),
    enabled: enabled,
  })
}

const fetchAccountTrackingTopicText = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.ACCOUNT_TRACKING);
  return response;
};

const useFetchAccountTrackingTopicText = (enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['fetch_account_tracking_topic_text'],
    queryFn: () => fetchAccountTrackingTopicText(),
    enabled: enabled
  });
};

const postAccountTrackingTopicText = async (data: { topic_data: string; description: string }): Promise<any> => {
  const response = await axiosInstance.post(PATHS.ACCOUNT_TRACKING, data);
  return response;
};

const useAddAccountTrackingTopicText = (handlerFunctions?: any) => {
  return useMutation<any, any, { topic_data: string; description: string }>({
    mutationKey: ['add_account_tracking_topic_text'],
    mutationFn: (data) => postAccountTrackingTopicText(data).then((res) => res),
    ...handlerFunctions
  });
};

const putAccountTrackingTopicText = async (data: { id: number; topic_data: string; description: string }): Promise<any> => {
  const response = await axiosInstance.put(PATHS.ACCOUNT_TRACKING, data);
  return response;
};

const useUpdateAccountTrackingTopicText = (handlerFunctions?: any) => {
  return useMutation<any, any, { id: number; topic_data: string; description: string }>({
    mutationKey: ['update_account_tracking_topic_text'],
    mutationFn: (data) => putAccountTrackingTopicText(data).then((res) => res),
    ...handlerFunctions
  });
};

const deleteAccountTrackingTopicText = async (id: number): Promise<any> => {
  const response = await axiosInstance.delete(PATHS.ACCOUNT_TRACKING, {
    data: { id },
  });
  return response;
};

const useDeleteAccountTrackingTopicText = (handlerFunctions?: any) => {
  return useMutation<any, any, { id: number }>({
    mutationKey: ['delete_account_tracking_topic_text'],
    mutationFn: (data) => deleteAccountTrackingTopicText(data.id).then((res) => res),
    ...handlerFunctions
  });
};

export interface IntegrationsAPIClient {
  readonly useGetIntegrations: (enabled: boolean) => any;
  readonly useGetCalendars: (enabled: boolean, impersonate_email?: string) => any;
  readonly useAuthoriseCalendar: (authoriseCalendarParams: { calendar_domain: string }, enabled: boolean) => any;
  readonly useAuthoriseSalesforce: (enabled: boolean) => any;
  readonly useAuthoriseCallback: (queryString: string, enabled: boolean) => any;
  readonly useCalendarConnectionMutation: (email_id: string, handlerFunctions: any) => any;
  readonly useCalendarDeleteMutation: (email_id: string, handlerFunctions: any) => any;
  readonly useFetchCalendar: (fetchCalendarParams: { email_id: string, calendar_domain: string, start_time: string, end_time: string }, enabled: boolean) => any;
  readonly useFetchAccountTrackingTopicText: (enabled: boolean) => any;
  readonly useAddAccountTrackingTopicText: (handlerFunctions?: any) => any;
  readonly useUpdateAccountTrackingTopicText: (handlerFunctions?: any) => any;
  readonly useDeleteAccountTrackingTopicText: (handlerFunctions?: any) => any;
}

const IntegrationsAPIClient: IntegrationsAPIClient = Object.freeze({
  useGetCalendars,
  useGetIntegrations,
  useAuthoriseCalendar,
  useAuthoriseSalesforce,
  useAuthoriseCallback,
  useCalendarConnectionMutation,
  useCalendarDeleteMutation,
  useFetchCalendar,
  useFetchAccountTrackingTopicText,
  useAddAccountTrackingTopicText,
  useUpdateAccountTrackingTopicText,
  useDeleteAccountTrackingTopicText
});

export default IntegrationsAPIClient;
